import React, { createContext, useState, useEffect, useContext } from 'react'
import {
  fetchActiveBanners,
  fetchStandings as fetchLeagueStandings,
  fetchAllPlayerTransactions,
  FeedItem,
  Feed,
  ShopProduct,
  fetchLeaderByStatList,
  fetchSeasonEvents,
} from '../../Api'
import {
  STATS_YEAR,
  EVENTS_YEAR,
  hasChampSeriesStarted as showChampSeries,
  STANDINGS_YEAR,
} from '../../Config/League/league.config'
import { champSeriesStatTitles, statTitles } from '../../Constants/player'
import { filterStandings, mapPlayerStat, SEASON_SEGMENT_NAMES } from '../../Utils'
import type { LeaderResult } from './types'
import type {
  Banner,
  LeaderData,
  TeamStanding,
  EventGQL,
  PlayerTransactions,
} from '../../Api'
import {
  checkHasChampSeriesStarted,
  getCurrentWeek,
} from '../../Utils/Events/eventHelpers'
import { getFeed } from '../../Api/Feed/feed.api'
import { SeasonContext } from '../SeasonContext'
import { fetchShopCollection } from '../../Api/Shop/shop.api'
import { SHOP_COLLECTIONS } from '../../Config/Shop/shop.config'
import { ShopProductSortKeys } from '../../Api/Shop/shop.utils'
import { StorageContext } from '../StorageContext'
import { STORAGE_NAMES } from '../../Constants'
import { SEASON_SELECTOR_TYPES } from '../../Constants/season'

type LeagueContextType = {
  events: EventGQL[]
  eventsLoading: boolean
  currentWeek: null | string
  standings: TeamStanding[]
  feed: Feed | null
  teamFeed: Feed
  isFetchingMainFeed: boolean
  isFetchingTeamFeed: boolean
  feedLoading: boolean
  leaders: LeaderResult[]
  banners: Banner[]
  bestSellingProducts: ShopProduct[]
  playerTransactions: PlayerTransactions[]
  fetchTeamFeed: (
    team: string,
    includeVideos: boolean,
    limit?: number
  ) => Promise<FeedItem[] | null> | null
  fetchStandings: () => Promise<void>
  fetchLeaders: () => Promise<LeaderResult[]>
  fetchPlayerTransaction: () => PlayerTransactions[] | Promise<PlayerTransactions[]>
  getTeamEvents: (team: string) => EventGQL[]
  updateEventInState: (event: EventGQL) => void
}

export const LeagueContext = createContext<LeagueContextType | null>(null)

export const LeagueProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { seasonSelect } = useContext(SeasonContext)!
  const standingsSelectorType = SEASON_SELECTOR_TYPES.STANDINGS
  const { isStorageReady, store, getStoredData } = useContext(StorageContext)!
  const [events, setEvents] = useState<EventGQL[]>([])
  const [eventsLoading, setEventsLoading] = useState<boolean>(true)
  const [currentWeek, setCurrentWeek] = useState<null | string>(null)
  const [standings, setStandings] = useState<TeamStanding[]>([])
  const [feed, setFeed] = useState<Feed | null>(null)
  const [teamFeed, setTeamFeed] = useState<Feed>({
    trending: [],
    latest: [],
  })
  const [isFetchingMainFeed, setIsFetchingMainFeed] = useState<boolean>(false)
  const [isFetchingTeamFeed, setIsFetchingTeamFeed] = useState<boolean>(false)
  const [feedLoading, setFeedLoading] = useState<boolean>(true)
  const [leaders, setLeaders] = useState<LeaderResult[]>([])
  const [playerTransactions, setPlayerTransactions] = useState<PlayerTransactions[]>([])
  const [banners, setBanners] = useState<Banner[]>([])
  const [bestSellingProducts, setBestSellingProducts] = useState<ShopProduct[]>([])
  const [hasChampSeriesStarted, setHasChampSeriesStarted] = useState<boolean>(false)

  useEffect(() => {
    if (isStorageReady) {
      fetchData()
    }
  }, [isStorageReady])

  const fetchData = async (): Promise<any> => {
    await Promise.all([
      fetchBanners(),
      fetchEvents(),
      fetchStandings(),
      fetchFeed(),
      fetchMainShopCollection(),
    ])
  }

  const fetchEvents = async () => {
    // First try to fill from storage
    getStoredEvents()
    try {
      const eventsRes = await fetchSeasonEvents(EVENTS_YEAR, showChampSeries)
      if (eventsRes && eventsRes.length > 0) {
        setEvents(eventsRes)
        setCurrentWeek(getCurrentWeek(eventsRes))
        setHasChampSeriesStarted(checkHasChampSeriesStarted(eventsRes))
        setEventsLoading(false)
        store(STORAGE_NAMES.seasonEvents, eventsRes)
      }
      setEventsLoading(false)
    } catch (err) {
      console.log('Error getting events', err)
      setEventsLoading(false)
    }
  }

  const getStoredEvents = async (): Promise<void> => {
    const storedData = await getStoredData(STORAGE_NAMES.seasonEvents)
    if (storedData?.data && storedData.data.length > 0) {
      setEvents(storedData.data)
      setEventsLoading(false)
    }
  }

  const fetchBanners = async () => {
    const { data, refetch } = await getStoredBanners()
    if (data && !refetch) return
    try {
      const bannerList = await fetchActiveBanners(true)
      if (bannerList?.length < 1) return
      setBanners(bannerList)
      store(STORAGE_NAMES.banners, bannerList)
    } catch (err) {
      console.log(err)
    }
  }

  const getStoredBanners = async (): Promise<{ data: any; refetch: boolean }> => {
    const storedData = await getStoredData(STORAGE_NAMES.banners, 300000)
    if (storedData?.data) {
      setBanners(storedData.data)
    }
    return storedData
  }

  const fetchMainShopCollection = async () => {
    const { data, refetch } = await getStoredShopCollection()
    if (data && !refetch) return
    //Fetch in background if needed
    try {
      const collection = await fetchShopCollection(
        SHOP_COLLECTIONS.MAIN,
        ShopProductSortKeys.BEST_SELLING,
        10
      )
      
      if (collection?.products && collection.products.length > 0) {
        setBestSellingProducts(collection.products)
        store(STORAGE_NAMES.home_shop_collection, collection.products)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getStoredShopCollection = async (): Promise<{ data: any; refetch: boolean }> => {
    const storedData = await getStoredData(STORAGE_NAMES.home_shop_collection, 1800000)
    if (storedData?.data) {
      setBestSellingProducts(storedData?.data)
    }
    return storedData
  }

  const fetchFeed = async () => {
    getCachedFeed()
    if (isFetchingMainFeed) return
    if (!feed) {
      setIsFetchingMainFeed(true)
    }
    const newFeed = await getFeed({ limit: 50, includeVideos: true })

    if (!newFeed) {
      setFeedLoading(false)
      return
    }
    setFeed(newFeed)
    isFetchingMainFeed && setIsFetchingMainFeed(false)
    feedLoading && setFeedLoading(false)
    store(STORAGE_NAMES.home_feed, newFeed)
    return
  }

  const getCachedFeed = async (): Promise<void> => {
    const { data } = await getStoredData(STORAGE_NAMES.home_feed)
    if (data) {
      setFeed(data)
      setFeedLoading(false)
    }
  }

  const fetchTeamFeed = async (
    team: string,
    includeVideos: boolean = false,
    limit?: number
  ) => {
    if (isFetchingTeamFeed) return null
    setIsFetchingTeamFeed(true)
    const feedParams = {
      teamId: team,
      includeVideos,
      limit,
    }
    try {
      const teamFeed = await getFeed(feedParams)
      if (!teamFeed) return null
      setTeamFeed(teamFeed)
      setIsFetchingTeamFeed(false)
      return teamFeed.team || []
    } catch (err) {
      console.log(err)
      setIsFetchingTeamFeed(false)
      return null
    }
  }

  const fetchStandings = async () => {
    let isCS =
      seasonSelect[standingsSelectorType].seasonType === SEASON_SEGMENT_NAMES.champseries
    if (!isCS) {
      const { refetch } = await getCachedStandings()
      if (!refetch) return
    }
    try {
      let teams = await fetchLeagueStandings(STANDINGS_YEAR, isCS)
      if (!teams) return
      teams = filterStandings(teams, seasonSelect[standingsSelectorType])
      setStandings(teams)
      if (!isCS) {
        store(`${STORAGE_NAMES.standings}_${seasonSelect.year}`, teams)
      }
    } catch (err) {
      console.log('Error getting standings', err)
    }
  }

  const getCachedStandings = async (): Promise<{ data: any; refetch: boolean }> => {
    const storedData = await getStoredData(
      `${STORAGE_NAMES.standings}_${seasonSelect.year}`,
      300000
    )
    if (storedData?.data) {
      const cleanStandings = filterStandings(
        storedData.data,
        seasonSelect[standingsSelectorType]
      )
      setStandings(cleanStandings)
    }
    return storedData
  }

  const fetchLeaders = async () => {
    if (leaders.length > 0) return leaders
    let titlesToUse = hasChampSeriesStarted ? champSeriesStatTitles : statTitles
    const seasonSegment = hasChampSeriesStarted
      ? SEASON_SEGMENT_NAMES.champseries
      : SEASON_SEGMENT_NAMES.regular
    try {
      let data = await fetchLeaderByStatList(STATS_YEAR, 1, seasonSegment, titlesToUse)
      if (data.length < 1) return []
      const filtered = data.reduce(function (result: LeaderData[], ldr) {
        if (ldr) {
          result.push(ldr)
          return result
        }
        return result
      }, [])
      const leaders = filtered.map((ldr) => {
        const result = {
          ...ldr,
          statName: mapPlayerStat(ldr.statType),
        }
        return result
      })
      setLeaders(leaders)
      return leaders
    } catch (err) {
      console.log('Error getting leaders', err)
      return []
    }
  }

  const fetchPlayerTransaction = async (): Promise<PlayerTransactions[]> => {
    if (playerTransactions.length > 0) {
      return playerTransactions
    }
    const pTransactions = await fetchAllPlayerTransactions()
    if (!pTransactions) return []
    const transactions = pTransactions.playerTransactions
    setPlayerTransactions(transactions)
    return transactions
  }

  const getTeamEvents = (team: string): EventGQL[] => {
    const teamEvents = events.filter(
      (event) =>
        event.homeTeam?.officialId === team || event.awayTeam?.officialId === team
    )
    return teamEvents
  }

  const updateEventInState = (event: EventGQL) => {
    setEvents(events.map((e) => (e.id === event.id ? event : e)))
    //Update in storage
    store(STORAGE_NAMES.seasonEvents, events)
  }

  return (
    <LeagueContext.Provider
      value={{
        events,
        eventsLoading,
        currentWeek,
        standings,
        feed,
        teamFeed,
        isFetchingMainFeed,
        isFetchingTeamFeed,
        feedLoading,
        leaders,
        banners,
        bestSellingProducts,
        playerTransactions,
        fetchTeamFeed,
        fetchStandings,
        fetchLeaders,
        fetchPlayerTransaction,
        getTeamEvents,
        updateEventInState
      }}
    >
      {children}
    </LeagueContext.Provider>
  )
}
